// START IMPORTS
// Hooks imports
import { useReport } from '@ltvco/refresh-lib/hooks';
// payments imports
import {
  ReactivationDaysLeft,
  uncancelBillingVariation2Value,
  uncancelBillingPbrFeatureName,
  uncancelBillingControlValue,
} from '@ltvco/refresh-lib/payments';
// Theme and UI imports
import { Stack, Box } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  DateUtil,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  DebugMenu,
  ReportFactory,
  ReportLoading,
  ReportOptions,
  SerializedPerson,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  PhoneSection as PhoneSectionV2,
  EmailSection as EmailSectionV2,
  AddressHistorySection as AddressHistorySectionV2,
  SocialSection as SocialSectionV2,
  JobsSection as JobsSectionV2,
  EducationSection as EducationSectionV2,
  ReportRating as ReportRatingV2,
  RelatedReportsSection as RelatedReportsSectionV2,
  NotesSection as NotesSectionV2,
  PotentialOwners as PotentialOwnersV2,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  ReportNavigationMenuProps,
  UsernameReportOverview,
  LizanoListItemDataSocial,
  ReportNullState,
  ReportActionsWithDateUpdated,
  ReportGeneratingScreen,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useFeatureIsOn, useFeatureValue } from '@ltvco/refresh-lib/vendors';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import { getSocialNavLinkData } from 'navLinkData/socialNavLinkData';

import { getHighConfidenceDisplayData } from './utils/getHighConfidenceDisplayData';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface SocialReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'username';
// END CONSTANTS

export function SocialReport({
  permalink,
  firstReportScreen,
}: SocialReportProps) {
  // START HOOKS_AND_QUERIES
  const queryResult = useReport(permalink);
  const { hash } = useLocation();
  const isInListV2Variation = useFeatureIsOn('prop-2834');
  const [, triggerUpdate] = useState(0);
  const isInHighConfidenceSectionsExperiment = useFeatureIsOn('RFRSH-4240');
  const uncancelFeatureValue = useFeatureValue(
    uncancelBillingPbrFeatureName,
    uncancelBillingControlValue
  );
  const uncancelFeatureIsOn = useFeatureIsOn(uncancelBillingPbrFeatureName);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: true,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const zeroed = isZeroed(queryResult);
  useScrollToSectionOnNavigate(hash, queryResult);

  const handleSetReportOptions = (newOptions: Partial<ReportOptions>) => {
    setReportOptions({ ...reportOptions, ...newOptions });
  };
  // END DERIVED_DATA_AND_UTILS

  // START EARLY_RETURNS
  if (firstReportScreen?.isActive) {
    return (
      <ReportGeneratingScreen
        reportType="username"
        ready={!queryResult.isLoading}
        onComplete={() => {
          firstReportScreen?.onComplete();
          triggerUpdate((n) => n + 1);
        }}
      />
    );
  }

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={9} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }

  const report = ReportFactory.create(queryResult?.data, 'username');

  if (!report) {
    return <ReportLoading menuItems={9} />;
  }

  if (!report?.data?.people && !report?.data?.people?.length) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const owner = report.getOwner(reportOptions);
  const {
    ownerName,
    emails,
    phones,
    educations,
    jobs,
    profiles,
    usernames,
    addresses,
    relatives,
  } = owner;
  const highConfidenceThreshold =
    reportOptions.higherConfidenceThreshold ||
    constants.config.higherConfidenceThreshold;
  const showHighConfidenceDataOnly = reportOptions.showHighConfidenceDataOnly;
  const { phonesForDisplay, emailsForDisplay, addressesForDisplay } =
    getHighConfidenceDisplayData({
      owner,
      showHighConfidenceDataOnly,
      highConfidenceThreshold,
    });
  const phoneItems = isInHighConfidenceSectionsExperiment
    ? phonesForDisplay
    : phones;
  const emailItems = isInHighConfidenceSectionsExperiment
    ? emailsForDisplay
    : emails;
  const addressItems = isInHighConfidenceSectionsExperiment
    ? addressesForDisplay
    : addresses;

  const socialNavLinkData = getSocialNavLinkData(
    owner,
    report?.data?.people?.length ?? 0,
    isInHighConfidenceSectionsExperiment
      ? {
          phones: phoneItems,
          emails: emailItems,
          addresses: addressItems,
        }
      : undefined
  );
  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';
  document.title = `${report.username} - BeenVerified`;

  const buildReportDataForListV2 = () => {
    const data: LizanoListItemDataSocial = {
      permalink,
      username: report.username || '',
    };

    if (report?.data?.people && report?.data?.people?.length) {
      data.owners = (report.data.people || []).map(
        (owner: SerializedPerson) => {
          return {
            first_name: owner?.identity?.names?.[0]?.parsed?.first || '',
            last_name: owner?.identity?.names?.[0]?.parsed?.last || '',
          };
        }
      );
    }

    return data;
  };

  const showUsernameSocialMedias = useFeatureIsOn('UsernameAPIService');
  const isUsernamePartialAddress = useFeatureIsOn('OAR-1706');
  const alternativeTitle = isUsernamePartialAddress
    ? 'Possible Locations'
    : undefined;

  const potentialOwners = report.data.people;
  // END DATA_PREPARATION

  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        {/* START DESKTOP_NAVIGATION */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Social Report"
            headerTitle={ownerName || report.username}
            menuItems={
              socialNavLinkData as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        {/* END DESKTOP_NAVIGATION */}

        {/* START MAIN_CONTENT */}
        <Box marginTop={0}>
          {/* START REPORT_ACTIONS */}
          <Box
            sx={{
              height: 30,
              backgroundColor: '#f8f8f8',
              position: 'sticky',
              marginBottom: '-10px',
              marginX: -1,
              top: 52,
              zIndex: 10,
              display: {
                xs: 'none',
                sm: 'block',
                md: 'block',
                lg: 'block',
              },
            }}
          />

          {uncancelFeatureIsOn &&
            uncancelFeatureValue === uncancelBillingVariation2Value && (
              <ReactivationDaysLeft
                direction="row"
                isInReport
                customPadding={'16px 0 16px 0'}
              />
            )}
          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Username"
            dateUpdated={reportUpdateDate}
            reportUpgraded={false}
            reportDataForListV2={
              isInListV2Variation ? buildReportDataForListV2() : undefined
            }
          />
          {/* END REPORT_ACTIONS */}

          {/* START REPORT_OVERVIEW */}
          <UsernameReportOverview
            showUsernameSocialMedias={showUsernameSocialMedias}
            dateUpdated={reportUpdateDate}
            reportType={reportType}
            username={report.username}
            showHighConfidenceToggle
            showHighConfidenceDataOnly={
              reportOptions.showHighConfidenceDataOnly
            }
            setShowHighConfidenceDataOnly={(value: boolean) =>
              handleSetReportOptions({ showHighConfidenceDataOnly: value })
            }
            reportDataForListV2={
              isInListV2Variation ? buildReportDataForListV2() : undefined
            }
          />
          {/* END REPORT_OVERVIEW */}

          {/* START POTENTIAL_OWNERS */}
          <Box mt={2.5}>
            <PotentialOwnersV2
              potentialOwners={potentialOwners || []}
              potentialOwnerIndex={reportOptions.potentialOwnerIndex}
              setPotentialOwnerIndex={(index: number) => {
                handleSetReportOptions({ potentialOwnerIndex: index });
              }}
              personName={ownerName || report.username}
              permalink={permalink}
              reportType={reportType}
            />
          </Box>
          {/* END POTENTIAL_OWNERS */}

          {/* START REPORT_SECTIONS */}
          <PhoneSectionV2
            personName={ownerName || report.username}
            phoneList={phoneItems}
            permalink={permalink}
          />
          <EmailSectionV2
            personName={ownerName || report.username}
            emailList={emailItems}
            permalink={permalink}
          />
          <AddressHistorySectionV2
            addresses={addressItems}
            personName={ownerName || report.username}
            permalink={permalink}
            alternateTitle={alternativeTitle}
          />
          <SocialSectionV2
            personName={ownerName || report.username}
            profiles={profiles}
            usernames={usernames}
            permalink={permalink}
          />
          <JobsSectionV2
            personName={ownerName || report.username}
            jobs={jobs}
            permalink={permalink}
          />
          <EducationSectionV2
            personName={ownerName || report.username}
            educations={educations}
            permalink={permalink}
          />

          <NotesSectionV2 permalink={permalink} />
          <RelatedReportsSectionV2
            reportType="username"
            personName={ownerName || report.username}
            potentialOwners={report.data.people}
            relatives={relatives}
            addresses={addresses}
            phones={phones}
            emails={emails}
            usernames={usernames}
          />
          <ReportRatingV2
            rating={report.data.meta?.rating || null}
            report_type={reportType}
          />
          {/* END REPORT_SECTIONS */}
        </Box>
        {/* END MAIN_CONTENT */}
      </Stack>

      {/* START MOBILE_NAVIGATION */}
      <ReportNavigationMobileMenu
        reportType="Social Report"
        headerTitle={ownerName || report.username}
        menuItems={socialNavLinkData as ReportNavigationMenuProps['menuItems']}
      />
      {/* END MOBILE_NAVIGATION */}

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
    </Box>
  );
  // END RENDER
}
